import React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay } from '@fortawesome/pro-solid-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'

export function parseNumber (numberString, defaultValue = null) {
  const number = parseFloat(numberString)
  return isNaN(number) ? defaultValue : number
}

export function parseJSON (jsonString) {
  if (!isValidJSON(jsonString)) {
    return null
  }

  return JSON.parse(jsonString)
}

export function isValidJSON (jsonString) {
  try {
    JSON.parse(jsonString)
    return true
  } catch {
    return false
  }
}

export function calculateRemainingTime (targetDate) {
  const now = DateTime.now()
  const end = DateTime.fromMillis(targetDate)
  const diff = end.diff(now, ['days', 'hours', 'minutes', 'seconds']).toObject()

  // Check if the remaining time is 0 or negative
  if (diff.days <= 0 && diff.hours <= 0 && diff.minutes <= 0 && diff.seconds <= 0) {
    return false
  }

  return {
    days: Math.floor(diff.days),
    hours: Math.floor(diff.hours),
    minutes: Math.floor(diff.minutes),
    seconds: Math.floor(diff.seconds)
  }
}

export const padNumber = (number, range = 2) => {
  return number.toString().padStart(range, '0')
}

export function getVideoId (videoURL) {
  let videoId = videoURL.split('yt=')[1]

  if (!videoId) {
    videoId = videoURL.split('v=')[1]
  }

  if (!videoId) {
    videoId = videoURL.split('vim=')[1]
  }

  if (!videoId) {
    return null
  }

  const ampersandPosition = videoId.indexOf('&')

  if (ampersandPosition !== -1) {
    return cleanVideoId(videoId.substring(0, ampersandPosition))
  }

  return cleanVideoId(videoId)
}

export function getVideoUrl (videoURL) {
  if (!videoURL && typeof videoURL !== 'string') return null

  const parsedParams = new URLSearchParams(videoURL.split('?')[1] || '')

  if (parsedParams.has('yt')) {
    const videoId = cleanVideoId(parsedParams.get('yt'))
    return `//img.youtube.com/vi/${videoId}/hqdefault.jpg`
  }

  if (parsedParams.has('v')) {
    const videoId = cleanVideoId(parsedParams.get('v'))
    return `//img.youtube.com/vi/${videoId}/hqdefault.jpg`
  }

  if (parsedParams.has('vim')) {
    const videoId = cleanVideoId(parsedParams.get('vim'))
    return `https://vumbnail.com/${videoId}.jpg`
  }

  return null
}

function cleanVideoId (id) {
  if (!id) return ''
  const stopChars = ['&', '?']
  const cutAt = stopChars
    .map(char => id.indexOf(char))
    .filter(i => i !== -1)
  if (!id || typeof id !== 'string') return ''
  return id.substring(0, cutAt.length ? Math.min(...cutAt) : id.length)
}

export const generateVideoUrlById = (videoId) => {
  if (!videoId) return null
  return `//img.youtube.com/vi/${cleanVideoId(videoId)}/hqdefault.jpg`
}

export const generateVideoThumb = (title, videoUrl, callback) => {
  const thumbUrl = isValidUrl(videoUrl) ? getVideoUrl(videoUrl) : generateVideoUrlById(videoUrl)
  const videoId = getVideoId(videoUrl)
  return (
    <button
      className='a11y__interactive--button media--video'
      onClick={callback}
      aria-label={`${title} button`}
      key={`video-button-${videoId}`}
    >
      <img
        alt={`${title}`}
        className='img-responsive'
        src={thumbUrl}
        key={`video-thumbnail-${videoId}`}
      />
      <FontAwesomeIcon icon={faPlay} />
    </button>
  )
}

export const isValidUrl = (string) => {
  try {
    const url = new URL(string)
    return !!url
  } catch (_) {
    return false
  }
}

export const formatDate = (date, format = 'dd/MM/yyyy') => {
  return DateTime.fromISO(date).toFormat(format)
}

export function formatDateForCal (timestamp) {
  const dateObject = new Date(timestamp)

  return dateObject.getFullYear() +
    padNumber(dateObject.getMonth() + 1, 2) +
    padNumber(dateObject.getDate(), 2) +
    'T' + padNumber(dateObject.getHours(), 2) +
    padNumber(dateObject.getMinutes(), 2) +
    padNumber(dateObject.getSeconds(), 2)
}

export function getTimeZoneDetails (tzid) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tzid, timeZoneName: 'short' })
    const now = new Date()
    const offsetMinutes = now.getTimezoneOffset()
    const sign = offsetMinutes > 0 ? '-' : '+'
    const absOffsetMinutes = Math.abs(offsetMinutes)
    const hours = String(Math.floor(absOffsetMinutes / 60)).padStart(2, '0')
    const minutes = String(absOffsetMinutes % 60).padStart(2, '0')
    const offsetFormatted = `${sign}${hours}${minutes}`

    // Get timezone abbreviation (e.g., AEST, PDT)
    const tzName = formatter.formatToParts(now).find((part) => part.type === 'timeZoneName')?.value || tzid

    return { tzId: tzid, tzOffset: offsetFormatted, tzName }
  } catch (error) {
    console.error('Invalid Timezone ID:', error)
    return { tzId: tzid, tzOffset: null, tzName: null }
  }
}

const cachedLocaleStrings = {}
export function millisToLocaleString (
  timestamp,
  timezone = 'system',
  format = { ...DateTime.DATETIME_MED, timeZoneName: 'short' },
  dateFormat = 'ccc, dd MMM yyyy, hh:mma ZZZZ',
  locale = 'en-AU'
) {
  if (!timestamp) return null
  const stringKey = `${timestamp}-${timezone}-${format.year}${format.month}${format.day}${format?.timeZoneName || ''}`
  const cachedLocaleString = cachedLocaleStrings[stringKey]

  if (cachedLocaleString) return cachedLocaleString

  const zone = timezone === 'system' ? DateTime.local().zoneName : timezone
  const date = DateTime.fromMillis(timestamp, { locale })
  const newString = date.setZone(zone).toFormat(dateFormat)
  const formattedDatetime = `${newString}`
  cachedLocaleStrings[stringKey] = formattedDatetime
  return formattedDatetime
}

export const extractVisibleText = (html) => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent || tempDiv.innerText || ''
}

export const extractVisibleTextFromFullHTML = (html) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const scripts = doc.querySelectorAll('script, iframe')
  scripts.forEach(script => script.remove())
  return doc.body.textContent || doc.body.innerText || ''
}

export const encodeHTML = (html) => {
  html = html.replace(/\[lt\]/g, '<').replace(/\[gt\]/g, '>')
  return html
}

export const formatNumber = (number, locale = 'en-US') => {
  return new Intl.NumberFormat(locale).format(number)
}

export const unFormatNumber = (number) => {
  if (typeof number === 'number') {
    return number
  }

  if (number === '') {
    return 0
  }

  return parseFloat(number.replace(/,/g, ''))
}

export const isArrayEmpty = (array) => {
  return !Array.isArray(array) || array.length === 0
}

export const googleMapsUrl = (data) => {
  const { street, suburb, postcode, state, country } = data
  const addressParts = [street, suburb, state + (postcode ? ` ${postcode}` : ''), country].filter(Boolean)

  return `https://www.google.com/maps/place/${addressParts.join(',').replaceAll(' ', '+')}`
}

export const formatAddress = (data) => {
  const { street, suburb, postcode, state, country } = data
  const addressParts = [street, suburb, state + (postcode ? ` ${postcode}` : ''), country].filter(Boolean)
  return addressParts.join(', ')
}

export const labelTooltip = (labelText, key, position = 'top') => {
  const regex = /\[.*?\]/g
  const matches = labelText.match(regex)

  if (!matches) return labelText

  const tooltipText = matches[0].replace('[', '').replace(']', '')
  const label = labelText.replace(matches[0], '').replace('(Optional)', '')
  const tooltip = <Tooltip id={`tooltip-${key}`}>{tooltipText}</Tooltip>
  return (
    <>
      <span key={key} className='form-label-text'>{label}</span>
      <OverlayTrigger placement={position} overlay={tooltip}>
        <FontAwesomeIcon icon={faInfoCircle}/>
      </OverlayTrigger>
    </>
  )
}
